import {
  Label,
  PaginatedCombobox,
  useApiClient,
  type PaginatedResponse,
  type PaginationParams,
} from '@startupquickstart/frontend';

interface Hotel {
  id: string;
  name: string;
  description?: string;
}

interface HotelComboboxProps {
  value?: string;
  onValueChange: (value: string, hotel?: Hotel) => void;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  pageSize?: number;
  required?: boolean;
  error?: string;
  where?: Record<string, unknown>;
  initialItem?: Hotel;
}

/**
 * Paginated combobox for selecting hotels
 * Reusable component for forms and filters
 */
export function HotelCombobox({
  value = '',
  onValueChange,
  label = 'Hotel',
  placeholder = 'Select hotel...',
  searchPlaceholder = 'Search hotels...',
  emptyMessage = 'No hotels found.',
  pageSize = 20,
  required = false,
  error,
  where,
  initialItem,
}: HotelComboboxProps) {
  const apiClient = useApiClient();

  /**
   * Fetch hotels with pagination and search
   */
  const getHotels = async (params: PaginationParams): Promise<PaginatedResponse<Hotel>> => {
    const response = await apiClient.get<PaginatedResponse<Hotel>>('/hotels', {
      params: {
        page: params.page,
        limit: params.limit,
        orderBy: { name: 'asc' },
        ...(params.search && { search: params.search }),
        ...(where && { where }),
      },
    });
    return response;
  };

  return (
    <div className="grid gap-2">
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <PaginatedCombobox<Hotel>
        getData={getHotels}
        getValueFromItem={item => item.id}
        getLabelFromItem={item => item.name}
        value={value}
        onValueChange={onValueChange}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        pageSize={pageSize}
        initialItem={initialItem}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
